/**
 * UR Brand System — Phase 2 session + autosave hook.
 *
 * Owns:
 *   - starting a Brand Project (`/api/brand-intake/start`)
 *   - persisting project_id + project_token in localStorage so the
 *     customer can resume on the same device
 *   - loading the project on mount and whenever token/id changes
 *   - debounced autosave via PATCH, with saving/saved/error states
 *   - deep setter that writes into a path (`a.b.c`) within the local
 *     draft, and queues a scoped backend update that merges server-side
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "ur_brand_intake_session_v1";

const readSession = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.project_id || !parsed?.project_token) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeSession = (s) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
};

const clearSession = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
};

// --- path utils ------------------------------------------------------------

const splitPath = (path) => path.split(".");

const getAt = (obj, path) => {
  const parts = splitPath(path);
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
};

const setAt = (obj, path, value) => {
  const parts = splitPath(path);
  const next = { ...(obj || {}) };
  let cur = next;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i];
    cur[k] = { ...(cur[k] || {}) };
    cur = cur[k];
  }
  cur[parts[parts.length - 1]] = value;
  return next;
};

// Build the smallest PATCH payload for a `path` change. The engine
// accepts either project-level scalars (business_stage, naming_status,
// existing_identity) OR nested `brand_data.<group>.<field>` writes.
export const buildPatchPayload = (path, value) => {
  if (path === "business_stage" || path === "naming_status" || path === "existing_identity") {
    return { [path]: value };
  }
  if (!path.startsWith("brand_data.")) return null;
  const inner = path.slice("brand_data.".length);
  const scopedPayload = { brand_data: setAt({}, inner, value) };
  return scopedPayload;
};

// Build a payload for a project-level answer value — regardless of how
// the renderer wraps it (e.g. explicit `unknown_stated`).
export const useIntakeSession = () => {
  const [session, setSession] = useState(() => readSession());
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  const headers = useMemo(() => session?.project_token
    ? { "X-Project-Token": session.project_token } : {}, [session?.project_token]);

  const api = useMemo(() => axios.create({
    baseURL: `${BASE}/api/brand-intake`,
    headers,
  }), [headers]);

  // --- start / load --------------------------------------------------------

  const startNew = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.post(`${BASE}/api/brand-intake/start`);
      const next = { project_id: data.project_id, project_token: data.project_token };
      writeSession(next);
      setSession(next);
      setProject(data.project);
    } catch (e) {
      setError("Could not start intake. Please retry.");
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    clearSession();
    setSession(null);
    setProject(null);
  }, []);

  // Load existing project on mount / session change
  useEffect(() => {
    let aborted = false;
    const load = async () => {
      if (!session?.project_id) { setLoading(false); return; }
      setLoading(true);
      try {
        const { data } = await api.get(`/${session.project_id}`);
        if (!aborted) setProject(data);
      } catch (e) {
        if (e?.response?.status === 404) {
          // Stale local token — clear and start fresh.
          clearSession();
          if (!aborted) setSession(null);
        } else if (!aborted) {
          setError("Could not load your intake. Please retry.");
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    load();
    return () => { aborted = true; };
  }, [api, session?.project_id]);

  // --- optimistic local setter + debounced autosave ------------------------

  const pendingRef = useRef({});
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);

  const flush = useCallback(async () => {
    if (!session?.project_id) return;
    if (inFlightRef.current) return;
    const payload = pendingRef.current;
    if (!payload || Object.keys(payload).length === 0) return;
    pendingRef.current = {};
    inFlightRef.current = true;
    setSaving(true); setError(null);
    try {
      const { data } = await api.patch(`/${session.project_id}`, payload);
      setProject(data);
      setSavedAt(Date.now());
    } catch (e) {
      // Put the batch back into pending so a retry picks it up.
      pendingRef.current = { ...payload, ...pendingRef.current };
      const status = e?.response?.status;
      if (status === 409) {
        setError("This intake has been submitted. Reload to see the latest state.");
      } else if (status === 422) {
        setError("Something in your answer didn't look right. Please adjust.");
      } else {
        setError("Autosave failed. We will retry shortly.");
        // schedule retry
        setTimeout(flush, 3000);
      }
    } finally {
      setSaving(false);
      inFlightRef.current = false;
    }
  }, [api, session?.project_id]);

  const queuePatch = useCallback((patchObj) => {
    // Merge pending — later writes to the same path win
    const merged = deepMerge(pendingRef.current, patchObj);
    pendingRef.current = merged;
    // Optimistic local update so the UI reflects the change immediately
    setProject((cur) => cur ? deepMerge(cur, applyProvenanceStamps(patchObj)) : cur);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 450);
  }, [flush]);

  // Convenience: write a config-driven field by path+value.
  const setFieldByPath = useCallback((path, value) => {
    const payload = buildPatchPayload(path, value);
    if (!payload) return;
    queuePatch(payload);
  }, [queuePatch]);

  // Submit — forces flush first so no draft bytes are lost.
  const submit = useCallback(async () => {
    if (!session?.project_id) return null;
    // Flush any pending edits synchronously
    if (timerRef.current) clearTimeout(timerRef.current);
    await flush();
    try {
      const { data } = await api.post(`/${session.project_id}/submit`, {});
      setProject(data);
      return { ok: true, project: data };
    } catch (e) {
      return { ok: false, status: e?.response?.status, detail: e?.response?.data?.detail };
    }
  }, [api, flush, session?.project_id]);

  // Cleanup on unmount
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return {
    hasSession: !!session,
    project, loading, saving, savedAt, error,
    startNew, reset, setFieldByPath, submit,
  };
};

// --- helpers ---------------------------------------------------------------

function deepMerge(a, b) {
  if (a == null) return b;
  if (b == null) return a;
  if (typeof a !== "object" || typeof b !== "object" || Array.isArray(a) || Array.isArray(b)) {
    return b;
  }
  const out = { ...a };
  for (const k of Object.keys(b)) out[k] = deepMerge(a[k], b[k]);
  return out;
}

// Local optimistic stamping: when we show the user's typed value
// immediately, wrap raw values into the provenanced shape the server
// will return. The real stamp lands when PATCH responds.
function applyProvenanceStamps(patch) {
  const stamp = (v) => ({ value: v, source: "customer", status: "customer_confirmed" });
  const walk = (node) => {
    if (node == null || typeof node !== "object") return stamp(node);
    if (Array.isArray(node)) {
      // list of complex objects → leave to backend; raw list → stamp
      if (node.length && typeof node[0] === "object" && !Array.isArray(node[0])) {
        return node.map(walk);
      }
      return stamp(node);
    }
    // Already-provenanced leaf (value key only) — leave intact
    if ("value" in node && !("brand_data" in node)) return stamp(node.value);
    const out = {};
    for (const k of Object.keys(node)) out[k] = walk(node[k]);
    return out;
  };
  // Project-level scalars are mirrored verbatim; brand_data is walked.
  const out = {};
  for (const k of Object.keys(patch)) {
    if (k === "brand_data") out[k] = walk(patch[k]);
    else out[k] = patch[k];
  }
  return out;
}
